// macOS Spotlight-style command palette. Cmd/Ctrl+K opens it; Esc closes.
// Mounts a single ranked list of "items" — actions (download transcript,
// download spec, …) and flow cards, ranked by query match. Keyboard
// driven: ↑/↓ moves selection, Enter runs, Tab cycles too. The shell
// owns item-building and shortcut wiring; this component is just the
// modal + keyboard logic.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../styles/CommandPalette.css';

export type PaletteItemKind = 'command' | 'card';

export interface PaletteItem {
  id: string;
  kind: PaletteItemKind;
  label: string;
  // Right-aligned hint (e.g. "[1] action" for a card, "transcript .md" for a command).
  hint?: string;
  // Extra searchable text, not displayed (e.g. statement type, target, BC).
  keywords?: string;
  // One-char or short emoji icon shown at the left.
  iconChar?: string;
  // Optional CSS class suffix for type-coloured chips on cards.
  swatch?: string;
  // Run when the user picks this item. Implementations should close the
  // palette themselves if they need to (most do).
  run: () => void;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  items: PaletteItem[];
}

// Cap search results so a fuzzy match doesn't dump 100 cards. The
// empty-query view (commands + all cards) skips the cap and relies on
// the .cmdk-list scroll instead — see filter() below.
const MAX_SEARCH_RESULTS = 14;

// Platform-aware shortcut label. Mac shows the glyph it expects;
// Windows/Linux see the spelled-out modifier.
const IS_MAC = typeof navigator !== 'undefined'
  && /Mac|iPod|iPhone|iPad/i.test(navigator.platform || navigator.userAgent || '');
const SHORTCUT_LABEL = IS_MAC ? '⌘K' : 'Ctrl+K';

const CommandPalette: React.FC<Props> = ({ isOpen, onClose, items }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Reset state when the palette opens; focus the input.
  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setSelectedIndex(0);
    // Two-frame delay so the input is in the DOM before we focus.
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [isOpen]);

  const filtered = useMemo(() => filter(items, query), [items, query]);

  // Keep selection in range as the result list shrinks.
  useEffect(() => {
    if (selectedIndex >= filtered.length) {
      setSelectedIndex(Math.max(0, filtered.length - 1));
    }
  }, [filtered.length, selectedIndex]);

  // Auto-scroll the highlighted row into view.
  useEffect(() => {
    if (!listRef.current) return;
    const row = listRef.current.children.item(selectedIndex) as HTMLElement | null;
    row?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (!isOpen) return null;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      if (filtered.length === 0) return;
      setSelectedIndex((i) => (i + 1) % filtered.length);
    } else if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
      e.preventDefault();
      if (filtered.length === 0) return;
      setSelectedIndex((i) => (i - 1 + filtered.length) % filtered.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = filtered[selectedIndex];
      if (item) item.run();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div className="cmdk-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cmdk-panel" role="dialog" aria-modal="true" aria-label="Command palette">
        <div className="cmdk-input-row">
          <span className="cmdk-input-icon" aria-hidden>{SHORTCUT_LABEL}</span>
          <input
            ref={inputRef}
            type="text"
            className="cmdk-input"
            placeholder="Search commands or cards…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelectedIndex(0); }}
            onKeyDown={handleKeyDown}
            spellCheck={false}
            autoComplete="off"
          />
          <button type="button" className="cmdk-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <ul ref={listRef} className="cmdk-list" role="listbox">
          {filtered.length === 0 ? (
            <li className="cmdk-empty">No matches for &ldquo;{query}&rdquo;</li>
          ) : (
            filtered.map((item, i) => {
              // Show a section header when the kind transitions, but
              // only in the empty-query (browse) view — search results
              // are already ranked across kinds, so dividers add noise.
              const showHeader = !query.trim() && (i === 0 || filtered[i - 1].kind !== item.kind);
              const headerLabel = item.kind === 'command' ? 'Actions' : 'Cards';
              return (
                <React.Fragment key={item.id}>
                  {showHeader && (
                    <li className="cmdk-section" role="presentation" aria-hidden>
                      {headerLabel}
                    </li>
                  )}
                  <li
                    role="option"
                    aria-selected={i === selectedIndex}
                    className={`cmdk-row cmdk-row--${item.kind}${i === selectedIndex ? ' cmdk-row--active' : ''}`}
                    onMouseEnter={() => setSelectedIndex(i)}
                    onMouseDown={(e) => { e.preventDefault(); item.run(); }}
                  >
                    <span className={`cmdk-row-icon${item.swatch ? ` cmdk-row-icon--${item.swatch}` : ''}`} aria-hidden>
                      {item.iconChar || (item.kind === 'command' ? '⚙' : '◇')}
                    </span>
                    <span className="cmdk-row-label">{item.label}</span>
                    {item.hint && <span className="cmdk-row-hint">{item.hint}</span>}
                  </li>
                </React.Fragment>
              );
            })
          )}
        </ul>

        <div className="cmdk-footer">
          <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
          <span><kbd>↵</kbd> run</span>
          <span><kbd>esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;

// ── Ranked filter ──────────────────────────────────────────────────
//
// Empty query: commands only (cards are too many to dump cold).
// Otherwise: substring-match across label + keywords, scored by
// position (prefix > word-start > anywhere) and kind (command edge
// over card when scores tie).

function filter(items: PaletteItem[], query: string): PaletteItem[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    // Empty query: show everything, commands first then cards. The
    // scrollable .cmdk-list handles long card lists; section headers
    // (rendered in JSX) divide the two groups visually.
    const cmds = items.filter((it) => it.kind === 'command');
    const cards = items.filter((it) => it.kind === 'card');
    return [...cmds, ...cards];
  }

  const scored: { it: PaletteItem; score: number }[] = [];
  for (const it of items) {
    const score = scoreMatch(q, it);
    if (score > 0) scored.push({ it, score });
  }
  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    // Commands ahead of cards on tie.
    if (a.it.kind !== b.it.kind) return a.it.kind === 'command' ? -1 : 1;
    return a.it.label.localeCompare(b.it.label);
  });
  return scored.slice(0, MAX_SEARCH_RESULTS).map((s) => s.it);
}

function scoreMatch(q: string, it: PaletteItem): number {
  const label = it.label.toLowerCase();
  const keywords = (it.keywords || '').toLowerCase();
  let best = 0;

  if (label === q) best = Math.max(best, 1000);
  if (label.startsWith(q)) best = Math.max(best, 500);

  // Word-start matches inside the label.
  const wordStarts = label.split(/[\s\-_/]+/);
  for (const w of wordStarts) {
    if (w === q) best = Math.max(best, 400);
    else if (w.startsWith(q)) best = Math.max(best, 250);
  }

  if (label.includes(q)) best = Math.max(best, 100);
  if (keywords.includes(q)) best = Math.max(best, 60);

  // Multi-word query: every word must hit somewhere.
  const tokens = q.split(/\s+/).filter(Boolean);
  if (tokens.length > 1) {
    const hay = `${label} ${keywords}`;
    const allHit = tokens.every((t) => hay.includes(t));
    if (allHit) best = Math.max(best, 80);
    else if (best < 100) return 0; // require all tokens to match
  }

  return best;
}
